import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { useDebounce } from "@quake/shared-ui/hooks/useDebounce";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import ArrowLeftIcon from "lucide-react/dist/esm/icons/arrow-left";
import Layers3Icon from "lucide-react/dist/esm/icons/layers-3";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import Pencil from "lucide-react/dist/esm/icons/pencil";
import SaveIcon from "lucide-react/dist/esm/icons/save";
import Trash2Icon from "lucide-react/dist/esm/icons/trash-2";
import { Button } from "@quake/shared-ui/components/ui/button";
import { toast } from "@quake/shared-ui/lib/sonner";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { trpc } from "~/utils/trpc";
import { SkillSetMembersEditor } from "../-components/skill-set-members-editor";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/skills/sets/$set_id")({
  component: SkillSetDetailsRouteComponent,
});

function SkillSetDetailsRouteComponent() {
  const { datasetSlug, set_id } = Route.useParams();
  useBreadCrumbs([
    { title: "Skills", link: `/${datasetSlug}/data/skills` },
    { title: "Skill sets", link: `/${datasetSlug}/data/skills/sets` },
    { title: "Skill set", link: `/${datasetSlug}/data/skills/sets/${set_id}` },
  ]);
  return <SkillSetDetailsPage datasetSlug={datasetSlug} setId={set_id} />;
}

export function SkillSetDetailsPage({ datasetSlug, setId }: { datasetSlug: string; setId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const presetQuery = useQuery(trpc.skills.getPresetById.queryOptions({ id: setId }));
  const preset = presetQuery.data;
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isPurposeEditing, setIsPurposeEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [skillQuery, setSkillQuery] = useState("");
  const debouncedSkillQuery = useDebounce(skillQuery, 300);

  const searchOptionsQuery = useQuery(
    trpc.skills.searchSkillOptions.queryOptions({
      query: debouncedSkillQuery,
      limit: 12,
    }),
  );

  const updatePreset = useMutation(
    trpc.skills.updatePreset.mutationOptions({
      onSuccess: async () => {
        await invalidateSetQueries(queryClient, setId)();
        toast.success("Skill set saved");
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );
  const setPresetMembers = useMutation(
    trpc.skills.setPresetMembers.mutationOptions({
      onSuccess: invalidateSetQueries(queryClient, setId),
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );
  const deletePreset = useMutation(
    trpc.skills.deletePreset.mutationOptions({
      onSuccess: invalidateSetQueries(queryClient, setId),
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  useEffect(() => {
    if (!preset) return;
    setName(preset.name);
    setPurpose(preset.purpose ?? "");
  }, [preset]);

  const trimmedName = name.trim();
  const trimmedPurpose = purpose.trim();
  const isMetadataDirty = trimmedName !== preset?.name || trimmedPurpose !== (preset?.purpose ?? "");

  const handleSave = useCallback(async () => {
    if (!preset || !trimmedName || !isMetadataDirty) return;
    await updatePreset.mutateAsync({
      id: preset.id,
      name: trimmedName,
      purpose: trimmedPurpose || null,
    });
  }, [preset, trimmedName, trimmedPurpose, isMetadataDirty, updatePreset]);

  const handleDelete = useCallback(async () => {
    if (!preset) return;
    await deletePreset.mutateAsync({ id: preset.id });
    await navigate({ to: "/$datasetSlug/data/skills/sets", params: { datasetSlug } });
  }, [preset, deletePreset, navigate, datasetSlug]);

  const handleReplaceMembers = useCallback(
    async (skillIds: string[]) => {
      if (!preset) return;
      await setPresetMembers.mutateAsync({
        preset_id: preset.id,
        members: skillIds.map((skill_id) => ({ skill_id, priority_weight: 1 })),
      });
    },
    [preset, setPresetMembers],
  );

  const handleAddSkill = useCallback(
    async (skillId: string) => {
      if (!preset) return;
      const nextIds = Array.from(new Set([...preset.members.map((m) => m.skill_id), skillId]));
      await handleReplaceMembers(nextIds);
    },
    [preset, handleReplaceMembers],
  );

  const handleRemoveSkill = useCallback(
    async (skillId: string) => {
      if (!preset) return;
      const nextIds = preset.members.filter((m) => m.skill_id !== skillId).map((m) => m.skill_id);
      await handleReplaceMembers(nextIds);
    },
    [preset, handleReplaceMembers],
  );

  if (presetQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading skill set…</div>;
  }

  if (!preset) {
    return (
      <div className="flex min-h-[calc(100vh-4.5rem)] items-center justify-center px-6">
        <div className="w-full max-w-xl rounded-2xl border border-black/8 bg-white p-8 text-center shadow-sm dark:border-white/8 dark:bg-white/5">
          <p className="text-[0.68rem] uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Missing</p>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
            This skill set no longer exists
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            It may have been deleted or moved out of this dataset.
          </p>
          <Button asChild className="mt-6">
            <Link to="/$datasetSlug/data/skills/sets" params={{ datasetSlug }}>
              Back to skill sets
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const isSavingMetadata = updatePreset.isPending;
  const isSavingMembers = setPresetMembers.isPending;
  const isDeleting = deletePreset.isPending;
  const isLocked = isSavingMetadata || isDeleting;

  return (
    <div className="min-h-[calc(100vh-4.5rem)]">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8">
        <div>
          <Link
            to="/$datasetSlug/data/skills/sets"
            params={{ datasetSlug }}
            className="inline-flex items-center gap-2 text-sm text-slate-600 transition hover:text-slate-950 dark:text-slate-300 dark:hover:text-slate-50"
          >
            <ArrowLeftIcon className="size-4" />
            Back to skill sets
          </Link>
        </div>

        <section className="rounded-2xl border border-black/8 bg-white p-6 shadow-sm dark:border-white/8 dark:bg-white/5">
          {/* Top bar: eyebrow + right-side controls */}
          <div className="flex items-center justify-between gap-4">
            <p className="text-[0.68rem] uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
              Skill set details
            </p>
            <div className="flex items-center gap-2">
              <div className="rounded-lg border border-black/8 bg-slate-50 px-3 py-1.5 text-sm text-slate-600 dark:border-white/8 dark:bg-white/5 dark:text-slate-300">
                <span className="inline-flex items-center gap-2">
                  <Layers3Icon className="size-4" />
                  {preset.members.length} skill{preset.members.length === 1 ? "" : "s"}
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={() => setIsDeleteDialogOpen(true)} disabled={isLocked}>
                <Trash2Icon className="mr-1.5 size-3.5" />
                Delete
              </Button>
            </div>
          </div>

          {/* Inline-editable name */}
          <div className="group mt-3 flex items-center gap-2">
            {isNameEditing ? (
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={(e) => e.target.select()}
                onBlur={() => setIsNameEditing(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setIsNameEditing(false);
                  }
                  if (e.key === "Escape") {
                    setName(preset.name);
                    setIsNameEditing(false);
                  }
                }}
                disabled={isLocked}
                className="w-full border-b border-slate-300 bg-transparent text-2xl font-semibold tracking-tight text-slate-950 outline-none focus:border-slate-500 dark:border-slate-600 dark:text-slate-50 dark:focus:border-slate-400"
              />
            ) : (
              <>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">{name}</h1>
                <button
                  onClick={() => !isLocked && setIsNameEditing(true)}
                  disabled={isLocked}
                  aria-label="Edit name"
                  className="rounded-md p-1 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-600 group-hover:opacity-100 dark:hover:bg-white/10 dark:hover:text-slate-300"
                >
                  <Pencil className="size-3.5" />
                </button>
              </>
            )}
          </div>

          {/* Inline-editable purpose */}
          <div className="mt-3">
            {isPurposeEditing ? (
              <textarea
                autoFocus
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                onBlur={() => setIsPurposeEditing(false)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setPurpose(preset.purpose ?? "");
                    setIsPurposeEditing(false);
                  }
                }}
                disabled={isLocked}
                rows={3}
                className="w-full resize-none border-b border-slate-200 bg-transparent text-sm leading-6 text-slate-600 outline-none focus:border-slate-400 dark:border-slate-700 dark:text-slate-300 dark:focus:border-slate-500"
              />
            ) : trimmedPurpose ? (
              <div
                role="button"
                tabIndex={0}
                onClick={() => !isLocked && setIsPurposeEditing(true)}
                onKeyDown={(e) => e.key === "Enter" && !isLocked && setIsPurposeEditing(true)}
                className="group/purpose flex cursor-text items-start gap-2"
              >
                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">{trimmedPurpose}</p>
                <Pencil className="mt-1 size-3.5 shrink-0 text-slate-400 opacity-0 transition-opacity group-hover/purpose:opacity-100" />
              </div>
            ) : (
              <button
                onClick={() => !isLocked && setIsPurposeEditing(true)}
                disabled={isLocked}
                className="flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              >
                <Pencil className="size-3.5 shrink-0" />
                Add a purpose to explain to recruiters when they should use this set
              </button>
            )}
          </div>

          {/* Save — only rendered when there are unsaved changes */}
          {isMetadataDirty && (
            <div className="mt-6 flex justify-end">
              <Button onClick={handleSave} disabled={!trimmedName || isSavingMetadata || isDeleting}>
                {isSavingMetadata ? (
                  <Loader2Icon className="mr-2 size-4 animate-spin" />
                ) : (
                  <SaveIcon className="mr-2 size-4" />
                )}
                {isSavingMetadata ? "Saving…" : "Save changes"}
              </Button>
            </div>
          )}
        </section>

        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete "{preset.name}"?</DialogTitle>
              <DialogDescription>
                This will permanently delete the skill set and remove it from any recruiter workflows. Skills in the
                catalog are not affected.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  await handleDelete();
                  setIsDeleteDialogOpen(false);
                }}
                disabled={isDeleting}
              >
                {isDeleting ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                {isDeleting ? "Deleting…" : "Delete skill set"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <SkillSetMembersEditor
          preset={preset}
          skillQuery={skillQuery}
          setSkillQuery={setSkillQuery}
          options={searchOptionsQuery.data ?? []}
          isLoading={searchOptionsQuery.isLoading}
          isSaving={isSavingMembers}
          onAddSkill={handleAddSkill}
          onRemoveSkill={handleRemoveSkill}
        />
      </div>
    </div>
  );
}

function invalidateSetQueries(queryClient: ReturnType<typeof useQueryClient>, presetId: string) {
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries(trpc.skills.listPresets.queryFilter()),
      queryClient.invalidateQueries(trpc.skills.getCatalogMeta.queryFilter()),
      queryClient.invalidateQueries(trpc.skills.listSkills.queryFilter()),
      queryClient.invalidateQueries({ queryKey: trpc.skills.getPresetById.queryOptions({ id: presetId }).queryKey }),
    ]);
  };
}
